import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ErrorModel, ProgressActivityModel, Session, SessionEvaluationInstrument } from '@tekman/onmat/models';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { StepsService } from '../service/steps.service';
import * as stepsActions from './steps.actions';
import { EMPTY, from, of } from 'rxjs';
import { appRoutes } from '@tekman/onmat/constants';
import { AuthorizationService, UserService } from '@tekman/onmat/service/generic';

@Injectable()
export class StepsEffects {
  constructor(
    private readonly _actions$: Actions,
    private readonly _router: Router,
    private readonly stepsService: StepsService,
    private readonly userService: UserService,
    private authService: AuthorizationService
  ) {}

  step$ = createEffect(() =>
    this._actions$.pipe(
      ofType(stepsActions.loadSessions),
      switchMap(action =>
        this.stepsService.getStep(action.itineraryId, action.stepId, action.groupId).pipe(
          map((sessions: Session[]) =>
            stepsActions.loadSessionsSuccess({
              stepId: action.stepId,
              sessions,
              groupId: action.groupId,
            })
          ),
          catchError((error: HttpErrorResponse) => {
            this._router.navigate([appRoutes.itineraries, action.groupId]);
            return of(stepsActions.loadSessionsFailure({ error: { message: error?.message, type: 'steps' } }));
          })
        )
      )
    )
  );

  evaluationInstruments$ = createEffect(() =>
    this._actions$.pipe(
      ofType(stepsActions.loadEvaluationInstruments),
      switchMap(action =>
        this.stepsService.getEvaluationInstruments(action.stepId, action.groupId).pipe(
          map((evaluationInstruments: SessionEvaluationInstrument[]) =>
            stepsActions.loadEvaluationInstrumentsSuccess({
              stepId: action.stepId,
              evaluationInstruments,
              groupId: action.groupId,
            })
          ),
          catchError((error: HttpErrorResponse) =>
            of(stepsActions.loadEvaluationInstrumentsFailure({ error: { message: error?.message, type: 'instruments' } }))
          )
        )
      )
    )
  );

  otherActivities$ = createEffect(() =>
    this._actions$.pipe(
      ofType(stepsActions.loadOtherActivities),
      switchMap(action =>
        this.stepsService.getOtherActivities(action.itineraryId, action.stepId, action.groupId).pipe(
          map((otherActivities: SessionEvaluationInstrument[]) =>
            stepsActions.loadOtherActivitiesSuccess({ stepId: action.stepId, otherActivities })
          ),
          catchError((error: HttpErrorResponse) =>
            of(stepsActions.loadOtherActivitiesFailure({ error: { message: error?.message, type: 'other' } }))
          )
        )
      )
    )
  );

  updateSteps$ = createEffect(() =>
    this._actions$.pipe(
      ofType(stepsActions.updateSteps),
      switchMap(action =>
        this.stepsService.updateStep(action.itineraryId, action.stepId, action.groupId, action.sessions).pipe(
          map((update: any) => {
            return stepsActions.updateStepsSuccess({ sessions: action.sessions });
          }),
          catchError((error: HttpErrorResponse) => of(stepsActions.updateStepsFailure()))
        )
      )
    )
  );

  progressStep$ = createEffect(() =>
    this._actions$.pipe(
      ofType(stepsActions.loadSessionsSuccess),
      switchMap(action => {
        if (this.userService.isTeacher()) {
          return EMPTY;
        } else {
          const activities = action.sessions
            .map(session => session.items!.map(items => items.activity))
            .flat()
            .filter(
              activity =>
                !activity?.isHidden &&
                !activity.resourceUrl &&
                (activity.typologyStyle === 'autoevaluacion' || activity.typologyStyle === 'ejercicio')
            );
          const uniqueActivities = activities.filter((activity, index, self) => index === self.findIndex(a => a.id === activity.id));
          return from(uniqueActivities).pipe(
            mergeMap(activity =>
              this.stepsService.getProgress(activity.id, action.groupId, this.authService.userId).pipe(
                map((progress: ProgressActivityModel) =>
                  stepsActions.loadStudentExerciseProgressSuccess({
                    exercisesProgress: {
                      ...progress,
                      id: activity.id,
                    },
                  })
                ),
                catchError((error: ErrorModel) => of(stepsActions.loadStudentExerciseProgressFailure({ error })))
              )
            )
          );
        }
      })
    )
  );

  progressEvaluationInstruments$ = createEffect(() =>
    this._actions$.pipe(
      ofType(stepsActions.loadEvaluationInstrumentsSuccess),
      switchMap(action => {
        if (this.userService.isTeacher()) {
          return EMPTY;
        } else {
          return from(
            action.evaluationInstruments.filter(
              session => !session.isHidden && !session.resourceUrl && session?.typologyStyle === 'ponte_a_prueba'
            )
          ).pipe(
            mergeMap(session =>
              this.stepsService.getProgress(session.id!, action.groupId, this.authService.userId).pipe(
                map((progress: ProgressActivityModel) => {
                  return stepsActions.loadStudentExerciseProgressSuccess({
                    exercisesProgress: {
                      ...progress,
                      id: session.id!,
                    },
                  });
                }),
                catchError((error: ErrorModel) => of(stepsActions.loadStudentExerciseProgressFailure({ error })))
              )
            )
          );
        }
      })
    )
  );
}
